import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X, PlayCircle, BookOpen, Bell } from "lucide-react";

const STORAGE_KEY = "credius_onboarding_seen_v1";

const GUIDE_BY_ROLE = {
  admin: "/guide/studio-admin_credius.pdf",
  fornitore: "/guide/avvocato_guida-credius.pdf",
  condomino: "/guide/cliente_credius.pdf",
};

/** Iter104 · Onboarding contestuale al primo accesso alla sezione Credius */
export function CrediusOnboarding() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [pref, setPref] = useState("entrambe");
  useEffect(() => {
    if (!user) return;
    const seen = localStorage.getItem(STORAGE_KEY);
    if (!seen) setVisible(true);
    // Carica preferenza corrente (per condòmino/fornitore non-avvocato)
    if (user.role === "condomino" || (user.role === "fornitore" && !user.is_avvocato)) {
      api.get("/credius/notifiche-prefs").then((r) => setPref(r.data.frequenza || "entrambe")).catch(() => {});
    }
  }, [user]);
  if (!visible || !user) return null;
  const guidaLink = GUIDE_BY_ROLE[user.role] || GUIDE_BY_ROLE.condomino;
  const roleLabel = user.role === "admin" ? "Studio" : user.role === "fornitore"
    ? (user.is_avvocato ? "Avvocato" : "Cliente") : "Cliente";
  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    setVisible(false);
  };
  return (
    <div data-testid="credius-onboarding"
      className="mb-4 rounded-xl border-2 border-sky-300 bg-gradient-to-br from-sky-50 to-indigo-50 p-4 relative">
      <button type="button" onClick={dismiss} data-testid="credius-onboarding-dismiss"
        className="absolute top-2 right-2 p-1 rounded hover:bg-white/50" aria-label="Chiudi">
        <X className="w-4 h-4 text-slate-500" />
      </button>
      <div className="text-sm font-bold text-indigo-900 mb-1">
        Benvenuto in Credius · {roleLabel}
      </div>
      <p className="text-sm text-slate-700 mb-3">
        Il modulo per gestire pratiche legali, notule pro-forma e ricorrenze. Ti bastano 60 secondi per
        capirne il flusso: guarda il video demo o scarica la guida operativa dedicata al tuo ruolo.
      </p>
      <div className="flex flex-wrap gap-2">
        <a href="/presentazione?p=credius" data-testid="credius-onboarding-video"
          className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-900 bg-white rounded-full px-3 py-1.5 hover:bg-indigo-50 border-2 border-indigo-200">
          <PlayCircle className="w-4 h-4" /> Guarda il video demo (75s)
        </a>
        <a href={guidaLink} target="_blank" rel="noreferrer" data-testid="credius-onboarding-guida"
          className="inline-flex items-center gap-2 text-sm font-semibold text-sky-900 bg-white rounded-full px-3 py-1.5 hover:bg-sky-50 border-2 border-sky-200">
          <BookOpen className="w-4 h-4" /> Scarica la guida ({roleLabel})
        </a>
        <button type="button" onClick={dismiss} data-testid="credius-onboarding-later"
          className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-800 ml-2">
          non mostrarmi più
        </button>
        {(user.role === "condomino" || (user.role === "fornitore" && !user.is_avvocato)) && (
          <button type="button" onClick={() => setShowPrefs(!showPrefs)}
            data-testid="credius-onboarding-prefs-toggle"
            className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-800 ml-2">
            <Bell className="w-3.5 h-3.5" /> notifiche
          </button>
        )}
      </div>
      {showPrefs && (
        <div className="mt-3 pt-3 border-t border-sky-200">
          <div className="text-xs font-bold uppercase text-sky-800 mb-2">Frequenza notifiche Credius</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {[
              { v: "entrambe", label: "Entrambe (istantanea + settimanale)" },
              { v: "istantanea", label: "Solo istantanee (documenti/messaggi)" },
              { v: "solo_settimanale", label: "Solo digest del lunedì" },
              { v: "off", label: "Disattiva (nessuna email)" },
            ].map((opt) => (
              <label key={opt.v}
                className={`flex items-center gap-2 text-sm p-2 rounded border-2 cursor-pointer ${pref === opt.v ? "bg-sky-100 border-sky-400" : "bg-white border-slate-200"}`}
                data-testid={`credius-onboarding-pref-${opt.v}`}>
                <input type="radio" name="credius-pref" value={opt.v} checked={pref === opt.v}
                  onChange={() => {
                    setPref(opt.v);
                    api.post("/credius/notifiche-prefs", { frequenza: opt.v })
                      .then(() => toast.success("Preferenza salvata"))
                      .catch(() => toast.error("Errore nel salvare"));
                  }}
                  className="w-4 h-4 accent-sky-700" />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
